import { useEffect, useRef } from 'react';

/**
 * Stage 0D — reload server truth when the page comes back.
 *
 * A phone that slept, a backgrounded tab, or a device that changed networks
 * misses realtime events while away; waiting for the next event would leave it
 * showing stale occupancy indefinitely. When the page becomes visible again,
 * regains focus, comes back online, or is restored from the back/forward
 * cache, `refetch` runs once. Signals that arrive together (visibility and
 * focus usually do) coalesce into a single refetch.
 *
 * The latest `refetch` is always used (no stale closure), and nothing fires
 * while the page is hidden or after unmount.
 */
export function useForegroundRecovery(
  refetch: () => void,
  { enabled = true, coalesceMs = 1000 }: { enabled?: boolean; coalesceMs?: number } = {},
): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!enabled) return;
    let lastFired = -Infinity;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const onSignal = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (timer || now - lastFired < coalesceMs) return;
      lastFired = now;
      timer = setTimeout(() => {
        timer = null;
        refetchRef.current();
      }, 0);
    };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) onSignal(); };

    document.addEventListener('visibilitychange', onSignal);
    window.addEventListener('focus', onSignal);
    window.addEventListener('online', onSignal);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onSignal);
      window.removeEventListener('focus', onSignal);
      window.removeEventListener('online', onSignal);
      window.removeEventListener('pageshow', onPageShow);
      if (timer) clearTimeout(timer);
    };
  }, [enabled, coalesceMs]);
}
